import { useEffect, useState } from 'react';
import {
  Save, ExternalLink, Plus, Trash2, ChevronUp, ChevronDown, Image as ImageIcon, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button, Card, CardHeader, CardTitle, CardContent, Input, useConfirm } from '@nepal-ecom/ui';
import { PageHeader } from '@/components/shared/PageHeader';
import { FileImageUploader } from '@/components/shared/FileImageUploader';
import { STOREFRONT_URL, cmsUrl, cmsFetch } from '@/lib/cms';

const CMS_URL = cmsUrl('hero');

// Minimal hero slide model. Earlier versions had icon/tone/accent pickers
// and a slash-syntax title; the storefront now uses the brand accent
// consistently, so the admin only needs to manage image + copy + CTAs.
interface HeroSlideData {
  title: string;
  titleAccent: string;        // kept for storefront back-compat (one global accent)
  description: string;
  primaryCtaLabel: string;
  primaryCtaHref: string;
  secondaryCtaLabel: string;
  secondaryCtaHref: string;
  image: string;
  // Legacy fields retained as optional so existing .cms-hero.json files keep
  // working — they're hidden from the new editor.
  eyebrow?: string;
  eyebrowIcon?: string;
  tone?: string;
}

const EMPTY_SLIDE: HeroSlideData = {
  title: '',
  titleAccent: '#D95100',
  description: '',
  primaryCtaLabel: 'Shop now',
  primaryCtaHref: '/products',
  secondaryCtaLabel: '',
  secondaryCtaHref: '',
  image: '',
  // Legacy fields the storefront HeroSlider still consumes — initialised so
  // brand-new slides render correctly without the admin needing to set them.
  eyebrow: '',
  eyebrowIcon: 'sparkles',
  tone: 'forest',
};

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-700 block mb-1.5">{label}</span>
      {children}
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </label>
  );
}

interface SlideProps {
  slide: HeroSlideData;
  index: number;
  total: number;
  onChange: (i: number, s: HeroSlideData) => void;
  onDelete: (i: number) => void;
  onMove: (from: number, to: number) => void;
}

function SlideEditor({ slide, index, total, onChange, onDelete, onMove }: SlideProps) {
  const set = <K extends keyof HeroSlideData>(key: K, val: HeroSlideData[K]) =>
    onChange(index, { ...slide, [key]: val });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <CardTitle className="text-base flex items-center gap-2">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-primary-50 text-primary-700 text-xs font-bold">
            {index + 1}
          </span>
          <span className="text-slate-700 font-medium">
            {slide.title?.trim() || 'Untitled slide'}
          </span>
        </CardTitle>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => onMove(index, index - 1)}
            disabled={index === 0}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors disabled:opacity-30"
            aria-label="Move up"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onMove(index, index + 1)}
            disabled={index === total - 1}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors disabled:opacity-30"
            aria-label="Move down"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(index)}
            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors ml-1"
            aria-label="Delete slide"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
          {/* Image left */}
          <FileImageUploader
            label="Background image"
            value={slide.image}
            onChange={(v) => set('image', v)}
            aspect="aspect-[4/3]"
            helperText="Wide cinematic image. 2000×1100 recommended."
          />

          {/* Copy right */}
          <div className="space-y-3.5">
            <Field label="Title">
              <Input
                value={slide.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder="Shop authentic Nepali"
              />
            </Field>

            <Field label="Description">
              <textarea
                value={slide.description}
                onChange={(e) => set('description', e.target.value)}
                rows={3}
                placeholder="A short tagline describing this slide."
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
              />
            </Field>

            <div className="pt-3 border-t border-slate-100">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Primary button
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Label">
                  <Input
                    value={slide.primaryCtaLabel}
                    onChange={(e) => set('primaryCtaLabel', e.target.value)}
                    placeholder="Shop now"
                  />
                </Field>
                <Field label="Link">
                  <Input
                    value={slide.primaryCtaHref}
                    onChange={(e) => set('primaryCtaHref', e.target.value)}
                    placeholder="/products"
                    className="font-mono text-xs"
                  />
                </Field>
              </div>
            </div>

            <div className="pt-3 border-t border-slate-100">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Secondary button <span className="font-normal text-slate-400">(optional)</span>
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Label">
                  <Input
                    value={slide.secondaryCtaLabel}
                    onChange={(e) => set('secondaryCtaLabel', e.target.value)}
                    placeholder="Learn more"
                  />
                </Field>
                <Field label="Link">
                  <Input
                    value={slide.secondaryCtaHref}
                    onChange={(e) => set('secondaryCtaHref', e.target.value)}
                    placeholder="/about"
                    className="font-mono text-xs"
                  />
                </Field>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function HeroCms() {
  const confirm = useConfirm();
  const [slides, setSlides] = useState<HeroSlideData[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await cmsFetch(CMS_URL, { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) setSlides(data as HeroSlideData[]);
        }
      } catch {
        toast.error('Could not reach storefront CMS.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setSlide = (i: number, s: HeroSlideData) =>
    setSlides((arr) => arr.map((x, idx) => (idx === i ? s : x)));

  const addSlide = () =>
    setSlides((arr) => [
      ...arr,
      {
        ...EMPTY_SLIDE,
        title: arr.length === 0 ? 'Shop authentic Nepali' : '',
        description:
          arr.length === 0 ? 'Verified makers, fair prices, delivered across Nepal.' : '',
      },
    ]);

  const deleteSlide = async (i: number) => {
    if (!(await confirm({
      title: 'Remove this slide?',
      confirmLabel: 'Remove',
      tone: 'danger',
    }))) return;
    setSlides((arr) => arr.filter((_, idx) => idx !== i));
  };

  const move = (from: number, to: number) =>
    setSlides((arr) => {
      if (to < 0 || to >= arr.length) return arr;
      const next = [...arr];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });

  const handleSave = async () => {
    if (slides.length === 0) {
      if (!(await confirm({
        title: 'Save with zero slides?',
        description: 'The hero will fall back to bundled defaults.',
        confirmLabel: 'Save anyway',
      }))) return;
    }
    setSaving(true);
    try {
      const res = await cmsFetch(CMS_URL, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(slides),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Hero saved', { description: 'Live on the storefront in a moment.' });
    } catch (err) {
      toast.error('Save failed', { description: (err as Error).message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="animate-fade-in space-y-5">
      <PageHeader
        title="Hero Slider"
        description="Rotating slides shown at the top of the homepage. Each slide has an image, headline, and up to two buttons."
        actions={
          <div className="flex items-center gap-2">
            {STOREFRONT_URL && (
              <Button asChild variant="outline" size="sm">
                <a href={STOREFRONT_URL} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" /> Open storefront
                </a>
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving || loading}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        }
      />

      {loading ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
            Loading slides…
          </CardContent>
        </Card>
      ) : slides.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ImageIcon className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-700">No slides yet</p>
            <p className="text-xs text-slate-500 mt-1 mb-4">
              The storefront will fall back to bundled defaults until you add one.
            </p>
            <Button size="sm" onClick={addSlide}>
              <Plus className="h-4 w-4" /> Add first slide
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {slides.map((s, i) => (
            <SlideEditor
              key={i}
              slide={s}
              index={i}
              total={slides.length}
              onChange={setSlide}
              onDelete={deleteSlide}
              onMove={move}
            />
          ))}
          <Button variant="outline" size="sm" className="w-full" onClick={addSlide}>
            <Plus className="h-4 w-4" /> Add another slide
          </Button>
        </>
      )}
    </div>
  );
}
